import React from 'react';
import { cn } from '@/shadcn/lib/utils';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/shadcn/components/ui/tooltip';
import { PROFILE_CONSTANTS } from './constants';

export interface ProfileMetadataRowProps {
  icon: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  /** Enable truncation with tooltip for text content */
  truncate?: boolean;
  /** Maximum width for truncated content */
  maxWidth?: string;
  /** Tooltip content (if different from children) */
  tooltipContent?: React.ReactNode;
}

/**
 * ProfileMetadataRow
 * ------------------
 * A simple row for profile metadata (icon + text), used in profile sections.
 *
 * - Icon: left-aligned, sized 12px-16px, colored via parent
 * - Text: text-xs, text-gray-500
 * - Spacing: flex, gap-1
 * - Optional CSS-based truncation with tooltip support
 * - Uses Social application's consistent tooltip styling
 * - Proper space consideration to the right of truncation
 */
const ProfileMetadataRow: React.FC<ProfileMetadataRowProps> = ({
  icon,
  children,
  className,
  truncate = false,
  maxWidth = PROFILE_CONSTANTS.DEFAULT_MAX_WIDTH,
  tooltipContent
}) => {
  const shouldShowTooltip = truncate && (tooltipContent || (typeof children === 'string' && children.length > PROFILE_CONSTANTS.TOOLTIP_CHAR_THRESHOLD));

  return (
    <div className={cn('text-xs text-gray-500 flex items-center gap-1', className)}>
      {icon}
      {truncate ? (
        shouldShowTooltip ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className={cn('truncate min-w-0 flex-1', maxWidth)}>
                {children}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">
              <div className="text-xs font-normal">
                {tooltipContent || children}
              </div>
            </TooltipContent>
          </Tooltip>
        ) : (
          <span className={cn('truncate min-w-0 flex-1', maxWidth)}>
            {children}
          </span>
        )
      ) : (
        children
      )}
    </div>
  );
};

export default ProfileMetadataRow;
